import Chart from "chart.js/auto";
import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";
import { bindDatePickerClick } from "../utils/date-input.js";

// 007-통계기능-포지션추적-설계서 7절: 수익률 페이지와 같은 기간 프리셋(일/주/월/년)을 공유한다.
// custom 범위는 이번 버전 범위 밖(6-5절).
type Range = "daily" | "weekly" | "monthly" | "yearly";

const RANGE_LABELS: Record<Range, string> = {
  daily: "일간",
  weekly: "주간",
  monthly: "월간",
  yearly: "연간",
};

interface ConditionFormulaPeriodPoint {
  period: string;
  strategy_tag: string;
  count: number;
}
interface HoldingDurationBucket {
  bucket: string;
  count: number;
}
interface StepDistributionPoint {
  step: number;
  count: number;
}
interface SellStepDistributionPoint {
  step: number;
  profit_count: number;
  loss_count: number;
}
interface StrategyTagRankingItem {
  strategy_tag: string;
  count: number;
}
interface IndexTrendPoint {
  period: string;
  avg_kospi: number | null;
  avg_kosdaq: number | null;
}

interface StatsSummary {
  env: Env;
  state: ScreenState;
  condition_formula_by_period: ConditionFormulaPeriodPoint[];
  holding_duration_histogram: HoldingDurationBucket[];
  buy_step_distribution: StepDistributionPoint[];
  sell_step_distribution: SellStepDistributionPoint[];
  strategy_tag_ranking: StrategyTagRankingItem[];
  index_trend_by_period: IndexTrendPoint[];
}

interface PositionListItem {
  position_id: string;
  stock_name: string | null;
  entry_ts: string;
  exit_ts: string;
  holding_days: number;
  max_buy_step: number | null;
  max_profit_step: number | null;
  max_loss_step: number | null;
  entry_strategy_tag: string | null;
  entry_kospi: number | null;
  entry_kosdaq: number | null;
  exit_kospi: number | null;
  exit_kosdaq: number | null;
  realized_pnl: number;
}

interface PositionListResult {
  env: Env;
  state: ScreenState;
  page: number;
  page_size: number;
  total: number;
  items: PositionListItem[];
}

// dataviz 가이드 검증 완료(node scripts/validate_palette.js, --mode dark --surface #1c1f2b, 이
// 앱의 --panel 값) — 다크 표면 기준 명도대/채도/CVD/명암비 6항목 전부 통과한 순서. 조건검색식처럼
// 몇 개가 뜰지 모르는 카테고리컬 시리즈에 쓴다. 8번째(기타 묶음) 이후로는 늘리지 않는다("절대
// 순환하지 않는다" 원칙) — 8개를 넘는 조건식은 "기타"로 합친다.
const CATEGORICAL_PALETTE = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
];
const OTHER_TAG_COLOR = CATEGORICAL_PALETTE[7]!;
const UNSPECIFIED_TAG_COLOR = "#9498ab"; // --text-dim — 정체성 채널(카테고리컬 8색)과 분리
const OTHER_LABEL = "기타";
const UNSPECIFIED_LABEL = "미지정";
// 이 앱의 기존 관례(main.css: "수익=빨강/손실=파랑")를 그대로 따른다 — 새 색을 만들지 않는다.
const PROFIT_COLOR = "#e5484d";
const LOSS_COLOR = "#4d84e5";

const AXIS_TICK_COLOR = "#9498ab";
const GRID_COLOR = "#2e3346";
const LEGEND_LABEL_COLOR = "#e8e8ee";

const CHART_BASE_OPTIONS = {
  responsive: true,
  scales: {
    x: { ticks: { color: AXIS_TICK_COLOR }, grid: { color: GRID_COLOR } },
    y: { ticks: { color: AXIS_TICK_COLOR }, grid: { color: GRID_COLOR }, beginAtZero: true },
  },
  plugins: {
    legend: { labels: { color: LEGEND_LABEL_COLOR } },
  },
} as const;

// 상위 7개 조건식만 고유 색을 받고, 나머지는 "기타"로 합친다(카테고리컬 색을 순환시키지 않는다는
// dataviz 원칙). "미지정"(조건식 없이 들어온 진입)은 카테고리컬 슬롯을 쓰지 않고 항상 중립색.
function buildTagPalette(rankingOrder: string[]): { colorOf: (tag: string) => string; topTags: Set<string> } {
  const realTags = rankingOrder.filter((t) => t !== UNSPECIFIED_LABEL);
  const topTags = new Set(realTags.slice(0, 7));
  const colorMap = new Map<string, string>();
  [...topTags].forEach((tag, i) => colorMap.set(tag, CATEGORICAL_PALETTE[i]!));
  const colorOf = (tag: string): string => {
    if (tag === UNSPECIFIED_LABEL) return UNSPECIFIED_TAG_COLOR;
    if (tag === OTHER_LABEL) return OTHER_TAG_COLOR;
    return colorMap.get(tag) ?? OTHER_TAG_COLOR;
  };
  return { colorOf, topTags };
}

function collapseTag(tag: string, topTags: Set<string>): string {
  if (tag === UNSPECIFIED_LABEL || topTags.has(tag)) return tag;
  return OTHER_LABEL;
}

// 007 설계서 7절: 6개 차트 카드(기간별×조건검색식 / 보유기간 분포 / 분할매수 최대차수 /
// 분할매도 최대차수(익절·손절) / 조건식별 랭킹 / 코스피·코스닥 평균 진입지수 추이) + 포지션 상세표.
export async function renderStats(container: HTMLElement): Promise<void> {
  let env: Env = "운영";
  let range: Range = "daily";
  let page = 1;
  // 사용자 요청: 거래내역/일별요약과 같은 기간검색을 포지션 상세표에만 추가한다(6개 차트의
  // 일/주/월/년 버킷 프리셋과는 별개 — 차트는 "추이", 이 표는 "개별 기록 나열"이라 목적이 다르다).
  let posFrom = "";
  let posTo = "";
  const chartInstances: Chart[] = [];

  container.innerHTML = `<h2 class="page-title">통계</h2>`;
  const tabsSlot = document.createElement("div");
  const rangeSlot = document.createElement("div");
  const noteSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, rangeSlot, noteSlot, resultsSlot);

  function renderControls(): void {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        env = newEnv;
        page = 1;
        void load();
      }),
    );

    const presetsRow = document.createElement("div");
    presetsRow.className = "range-presets";
    (Object.keys(RANGE_LABELS) as Range[]).forEach((r) => {
      const btn = document.createElement("button");
      btn.textContent = RANGE_LABELS[r];
      btn.className = r === range ? "active" : "";
      btn.addEventListener("click", () => {
        range = r;
        void load();
      });
      presetsRow.appendChild(btn);
    });
    rangeSlot.replaceChildren(presetsRow);

    // 007 설계서 v1.2(검토 반영 (i)): 기존 체결은 전부 position_id가 없어 배포 직후 화면이
    // 비어 보일 수 있다 — 버그로 오인하지 않도록 안내한다.
    const note = document.createElement("p");
    note.style.color = "var(--text-dim)";
    note.style.fontSize = "0.85em";
    note.textContent = "이 통계는 v1.7 배포 이후 발생한 매매부터 집계됩니다.";
    noteSlot.replaceChildren(note);
  }

  function destroyCharts(): void {
    for (const c of chartInstances) c.destroy();
    chartInstances.length = 0;
  }

  function makeChartCard(title: string): { card: HTMLElement; canvas: HTMLCanvasElement } {
    const card = document.createElement("div");
    card.className = "card";
    const h = document.createElement("h3");
    h.textContent = title;
    h.style.marginTop = "0";
    const canvas = document.createElement("canvas");
    canvas.style.maxHeight = "320px";
    card.append(h, canvas);
    return { card, canvas };
  }

  function fmtNum(v: number | null): string {
    return v === null ? "—" : v.toLocaleString("ko-KR", { maximumFractionDigits: 2 });
  }

  async function load(): Promise<void> {
    renderControls();

    const params = new URLSearchParams({ env, range });
    const posParams = new URLSearchParams({ env, page: String(page) });
    if (posFrom) posParams.set("from", posFrom);
    if (posTo) posParams.set("to", posTo);
    let summary: StatsSummary;
    let positions: PositionListResult;
    try {
      [summary, positions] = await Promise.all([
        api.get<StatsSummary>(`/api/stats/summary?${params.toString()}`),
        api.get<PositionListResult>(`/api/stats/positions?${posParams.toString()}`),
      ]);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();
    // Chart.js는 캔버스가 실제 DOM에 붙어있어야 크기를 제대로 잡는다 — 카드/캔버스는 먼저 만들어
    // frag에 담아두고, resultsSlot.replaceChildren(frag)로 라이브 DOM에 붙인 "다음"에 생성자를
    // 호출한다(daily-summary.ts/performance.ts와 같은 순서). 그래서 여기서는 생성자 호출을
    // 클로저로만 모아두고, 아래에서 한꺼번에 실행한다.
    const chartBuilders: Array<() => Chart> = [];

    const banner = renderEmptyStateBanner(summary.state, env, (newEnv) => {
      env = newEnv;
      page = 1;
      void load();
    });
    if (banner) frag.appendChild(banner);

    const grid = document.createElement("div");
    grid.className = "stats-grid";

    const { colorOf, topTags } = buildTagPalette(summary.strategy_tag_ranking.map((r) => r.strategy_tag));

    // 1) 기간별×조건검색식 진입 횟수 — 사용자가 명시적으로 요청한 메인 차트. 누적 막대.
    {
      const { card, canvas } = makeChartCard("기간별 조건검색식 진입 횟수");
      grid.appendChild(card);
      const periods = [...new Set(summary.condition_formula_by_period.map((p) => p.period))];
      const perPeriodTag = new Map<string, Map<string, number>>();
      for (const period of periods) perPeriodTag.set(period, new Map());
      for (const row of summary.condition_formula_by_period) {
        const tag = collapseTag(row.strategy_tag, topTags);
        const m = perPeriodTag.get(row.period)!;
        m.set(tag, (m.get(tag) ?? 0) + row.count);
      }
      const tags = [...new Set(summary.condition_formula_by_period.map((r) => collapseTag(r.strategy_tag, topTags)))];
      const datasets = tags.map((tag) => ({
        label: tag,
        data: periods.map((p) => perPeriodTag.get(p)?.get(tag) ?? 0),
        backgroundColor: colorOf(tag),
        stack: "entries",
        maxBarThickness: 24,
      }));
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "bar",
          data: { labels: periods, datasets },
          options: { ...CHART_BASE_OPTIONS, scales: { ...CHART_BASE_OPTIONS.scales, x: { ...CHART_BASE_OPTIONS.scales.x, stacked: true }, y: { ...CHART_BASE_OPTIONS.scales.y, stacked: true } } },
        }),
      );
    }

    // 2) 보유기간 분포.
    {
      const { card, canvas } = makeChartCard("보유기간 분포");
      grid.appendChild(card);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "bar",
          data: {
            labels: summary.holding_duration_histogram.map((b) => b.bucket),
            datasets: [
              {
                label: "포지션 수",
                data: summary.holding_duration_histogram.map((b) => b.count),
                backgroundColor: CATEGORICAL_PALETTE[0],
                maxBarThickness: 24,
              },
            ],
          },
          options: { ...CHART_BASE_OPTIONS, plugins: { legend: { display: false } } },
        }),
      );
    }

    // 3) 분할매수 최대차수 분포.
    {
      const { card, canvas } = makeChartCard("분할매수 최대차수 분포");
      grid.appendChild(card);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "bar",
          data: {
            labels: summary.buy_step_distribution.map((s) => `${s.step}차`),
            datasets: [
              {
                label: "포지션 수",
                data: summary.buy_step_distribution.map((s) => s.count),
                backgroundColor: CATEGORICAL_PALETTE[0],
                maxBarThickness: 24,
              },
            ],
          },
          options: { ...CHART_BASE_OPTIONS, plugins: { legend: { display: false } } },
        }),
      );
    }

    // 4) 분할매도 최대차수 분포(익절/손절 분리 — 007 설계서 v1.1, 검토 반영 (b)).
    {
      const { card, canvas } = makeChartCard("분할매도 최대차수 분포 (익절/손절)");
      grid.appendChild(card);
      const steps = summary.sell_step_distribution.map((s) => `${s.step}차`);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "bar",
          data: {
            labels: steps,
            datasets: [
              {
                label: "익절",
                data: summary.sell_step_distribution.map((s) => s.profit_count),
                backgroundColor: PROFIT_COLOR,
                maxBarThickness: 24,
              },
              {
                label: "손절",
                data: summary.sell_step_distribution.map((s) => s.loss_count),
                backgroundColor: LOSS_COLOR,
                maxBarThickness: 24,
              },
            ],
          },
          options: CHART_BASE_OPTIONS,
        }),
      );
    }

    // 5) 조건검색식별 총 진입횟수 랭킹(기간 구분 없음) — 차트1과 같은 색 매핑을 공유해 정체성을 유지한다.
    {
      const { card, canvas } = makeChartCard("조건검색식별 총 진입횟수 랭킹");
      grid.appendChild(card);
      const collapsed = new Map<string, number>();
      for (const r of summary.strategy_tag_ranking) {
        const tag = collapseTag(r.strategy_tag, topTags);
        collapsed.set(tag, (collapsed.get(tag) ?? 0) + r.count);
      }
      const sorted = [...collapsed.entries()].sort((a, b) => b[1] - a[1]);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "bar",
          data: {
            labels: sorted.map(([tag]) => tag),
            datasets: [
              {
                label: "진입 횟수",
                data: sorted.map(([, count]) => count),
                backgroundColor: sorted.map(([tag]) => colorOf(tag)),
                maxBarThickness: 24,
              },
            ],
          },
          options: { ...CHART_BASE_OPTIONS, indexAxis: "y", plugins: { legend: { display: false } } },
        }),
      );
    }

    // 6) 코스피/코스닥 평균 진입지수 추이 — dataviz 가이드("두 측정치가 스케일이 다르면 이중축 대신
    // 별도 차트/스몰멀티플") 반영: 코스피(~2500)와 코스닥(~800)을 한 축에 같이 그리지 않고
    // 두 개의 단일 시리즈 라인 차트로 나눈다.
    {
      const { card, canvas } = makeChartCard("코스피 평균 진입지수 추이");
      grid.appendChild(card);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "line",
          data: {
            labels: summary.index_trend_by_period.map((p) => p.period),
            datasets: [
              {
                label: "코스피",
                data: summary.index_trend_by_period.map((p) => p.avg_kospi),
                borderColor: CATEGORICAL_PALETTE[0],
                backgroundColor: "rgba(57,135,229,0.15)",
                fill: true,
                pointRadius: 3,
              },
            ],
          },
          options: { ...CHART_BASE_OPTIONS, plugins: { legend: { display: false } }, scales: { ...CHART_BASE_OPTIONS.scales, y: { ...CHART_BASE_OPTIONS.scales.y, beginAtZero: false } } },
        }),
      );
    }
    {
      const { card, canvas } = makeChartCard("코스닥 평균 진입지수 추이");
      grid.appendChild(card);
      chartBuilders.push(() =>
        new Chart(canvas, {
          type: "line",
          data: {
            labels: summary.index_trend_by_period.map((p) => p.period),
            datasets: [
              {
                label: "코스닥",
                data: summary.index_trend_by_period.map((p) => p.avg_kosdaq),
                borderColor: CATEGORICAL_PALETTE[1],
                backgroundColor: "rgba(217,89,38,0.15)",
                fill: true,
                pointRadius: 3,
              },
            ],
          },
          options: { ...CHART_BASE_OPTIONS, plugins: { legend: { display: false } }, scales: { ...CHART_BASE_OPTIONS.scales, y: { ...CHART_BASE_OPTIONS.scales.y, beginAtZero: false } } },
        }),
      );
    }

    frag.appendChild(grid);

    // 포지션 상세표 — 진입/청산 시각, 실현손익, 진입·청산 시점 지수(007 설계서 6-4절 ⑥).
    // 포지션 상세표 기간검색(사용자 요청) — daily-summary.ts/trades.ts와 같은 날짜 범위 입력.
    const posDateRow = document.createElement("div");
    posDateRow.className = "custom-range-row";
    posDateRow.innerHTML = `
      <input type="date" id="stats-pos-from" value="${posFrom}" />
      <span class="range-sep">~</span>
      <input type="date" id="stats-pos-to" value="${posTo}" />
      <button class="btn-secondary" id="stats-pos-apply">조회</button>
      ${posFrom || posTo ? `<button class="btn-secondary" id="stats-pos-reset">전체보기</button>` : ""}
    `;
    bindDatePickerClick(posDateRow.querySelector<HTMLInputElement>("#stats-pos-from")!);
    bindDatePickerClick(posDateRow.querySelector<HTMLInputElement>("#stats-pos-to")!);
    posDateRow.querySelector<HTMLButtonElement>("#stats-pos-apply")!.addEventListener("click", () => {
      posFrom = posDateRow.querySelector<HTMLInputElement>("#stats-pos-from")!.value;
      posTo = posDateRow.querySelector<HTMLInputElement>("#stats-pos-to")!.value;
      page = 1;
      void load();
    });
    posDateRow.querySelector<HTMLButtonElement>("#stats-pos-reset")?.addEventListener("click", () => {
      posFrom = "";
      posTo = "";
      page = 1;
      void load();
    });
    frag.appendChild(posDateRow);

    const tableCard = document.createElement("div");
    tableCard.className = "card";
    if (positions.items.length === 0) {
      tableCard.innerHTML = `<p style="color:var(--text-dim)">표시할 포지션이 없습니다.</p>`;
    } else {
      const scrollWrap = document.createElement("div");
      scrollWrap.className = "table-scroll";
      const table = document.createElement("table");
      table.innerHTML = `
        <thead><tr>
          <th>진입</th><th>청산</th><th>종목명</th><th>보유일수</th><th>매수차수</th><th>익절차수</th><th>손절차수</th>
          <th>조건식</th><th>진입 코스피</th><th>진입 코스닥</th><th>청산 코스피</th><th>청산 코스닥</th><th>실현손익</th>
        </tr></thead>
      `;
      const tbody = document.createElement("tbody");
      tbody.innerHTML = positions.items
        .map(
          (p) => `
        <tr>
          <td>${p.entry_ts}</td>
          <td>${p.exit_ts}</td>
          <td>${p.stock_name ?? "—"}</td>
          <td>${p.holding_days.toFixed(1)}</td>
          <td>${p.max_buy_step ?? "—"}</td>
          <td>${p.max_profit_step ?? "—"}</td>
          <td>${p.max_loss_step ?? "—"}</td>
          <td>${p.entry_strategy_tag ?? UNSPECIFIED_LABEL}</td>
          <td>${fmtNum(p.entry_kospi)}</td>
          <td>${fmtNum(p.entry_kosdaq)}</td>
          <td>${fmtNum(p.exit_kospi)}</td>
          <td>${fmtNum(p.exit_kosdaq)}</td>
          <td>${fmtNum(p.realized_pnl)}</td>
        </tr>`,
        )
        .join("");
      table.appendChild(tbody);
      scrollWrap.appendChild(table);
      tableCard.appendChild(scrollWrap);
    }
    frag.appendChild(tableCard);

    const totalPages = Math.max(1, Math.ceil(positions.total / positions.page_size));
    const pagination = document.createElement("div");
    pagination.className = "pagination";
    const prevBtn = document.createElement("button");
    prevBtn.textContent = "이전";
    prevBtn.disabled = page <= 1;
    prevBtn.addEventListener("click", () => {
      page -= 1;
      void load();
    });
    const info = document.createElement("span");
    info.style.color = "var(--text-dim)";
    info.textContent = `${page} / ${totalPages} (총 ${positions.total}건)`;
    const nextBtn = document.createElement("button");
    nextBtn.textContent = "다음";
    nextBtn.disabled = page >= totalPages;
    nextBtn.addEventListener("click", () => {
      page += 1;
      void load();
    });
    pagination.append(prevBtn, info, nextBtn);
    frag.appendChild(pagination);

    resultsSlot.replaceChildren(frag);
    // 이전 렌더의 차트 인스턴스는 이 시점(캔버스가 이미 새 걸로 교체된 뒤)에 안전하게 destroy하고,
    // 방금 라이브 DOM에 붙은 새 캔버스로 이번 렌더의 차트를 생성한다.
    destroyCharts();
    for (const build of chartBuilders) chartInstances.push(build());
  }

  await load();
}
